import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ApplicationCooLayout from '../../layout/application-coo-layout';
import { P2Button, P2ButtonGroup, P2Modal, P2TextDescription } from 'react-bootstrap-common-script';
import { AddressSelection } from '../../components/address-selection';
import { useAppDispatch, useAppSelector } from '../../redux/hook';
import { Properties } from '../../models/polymer/Properties.model';
import { ApplicationStart } from '../../models/classes/ApplicationStart.class';
import { useForm } from '../../hooks/use-form';
import { useNavigate } from 'react-router-dom';
import { updateApplicationStartFormDataThunk } from '../../redux/slices/application-start-slice';
import { useGetOngoingApplicationQuery } from '../../redux/services/getOngoingApplicationsStatus';
import { Helpers } from '../../utils/helper';
import useCheckSmdByHdbRef from '../../hooks/use-check-sms-by-hdb-ref';
import useCheckHdbProperties from '../../hooks/use-check-hdb-properties';
import { API_ApplicationRemove, API_EligibilityDebugLog } from '../../utils/api';
import { PolymerAxios } from '../../utils/axios';
import { setActiveApplications } from '../../redux/slices/active-applications-slice';
import { LottieLoader } from '../../components/loader';
import config from '../../config';

function PropertySelection() {
  const searchParams = new URLSearchParams(window.location.search);
  const hdbRef = searchParams.get('hdb_ref');
  const route = searchParams.get('route');

  const navigate = useNavigate();
  const AppDispatch = useAppDispatch();
  const [ isInit, setIsInit ] = useState<boolean>(false);
  const [ continueDraftConfirmation, setContinueDraftConfirmation ] = useState<boolean>(false);
  const properties = useAppSelector(state => state.app.GenuinePolymerSlice.properties);
  const originalLoginUser = useAppSelector(state => state.auth.AuthenticationSlice.originalLoginUser);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);

  const { data: ongoingApplications } = useGetOngoingApplicationQuery({uinfin: originalLoginUser?.uinfin, singpass_token: singpassToken ?? ""});
  const startApplicationFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const { values, setFormValue }: { handleChange: any, values: ApplicationStart, formValid: boolean, setFormValue: (update: any) => void } = useForm(new ApplicationStart(), () => { }, () => { });
  const { isError: isRdbError } = useCheckHdbProperties(originalLoginUser?.uinfin ?? "", "A");
  const { smsInfo, isLoading, isError, hasOutstandingFees, mamsEligibilityData, hasInitiatedApplication } = useCheckSmdByHdbRef(values.selected_hdb_property?.hdb_ref ?? "", "A");
  const [ hasOutstandingFeesModal, setHasOutstandingFeesModal ] = useState<boolean>(false);
  const [ hasApplicationInitiatedModal, setHasApplicationInitiatedModal ] = useState<boolean>(false);
  const [ hasSERSPostalCode1Modal, setHasSERSPostalCode1Modal ] = useState<boolean>(false);
  const [ hasSERSPostalCode2Modal, setHasSERSPostalCode2Modal ] = useState<boolean>(false);
  const [ isShowingOngoingTransactionModal, setIsShowingOngoingTransactionModal ] = useState<boolean>(false);
  const existingBb13Applications = useAppSelector(state => state.app.Bb13ApplicationsSlice.bb13Applications);
  const isBtnValid = useMemo((): boolean => {
    return values.selected_hdb_property !== undefined;
  }, [values]);

  const is_R2_151_Block_enabled = true; // R2-151: Block applications for specific postal codes due to the announcement of SERS

  const activeApplications = useMemo(() => {
    const nonEligibleStatuses = ["APPLICATION_APPROVED", "APPLICATION_COMPLETED", "APPLICATION_REJECTED", "APPLICATION_CANCELLED", "ARCHIVED", "CANCELLED", "REJECTED", "CLOSED", "PREPARE_FOR_COMPLETION_COMPLETE"];
    return ongoingApplications?.filter(item => !nonEligibleStatuses.includes(item.application_status) && item.hdb_ref === values?.selected_hdb_property?.hdb_ref) ?? [];
  }, [ongoingApplications, values])

  const getDraftApplicationDate = useMemo(()=>{
    if(activeApplications?.length > 0 && activeApplications[0]?.application_create_date){
       return Helpers.formatDate(activeApplications[0].application_create_date);
    }else{
      return '-';
    }

  },[activeApplications])

  const filteredProperties = useMemo(() => {
    if (!hdbRef) return properties;

    return properties.filter(property => property.hdb_ref === hdbRef);
  }, [hdbRef, properties]);

  useEffect(() => {
    AppDispatch(setActiveApplications(activeApplications));
  }, [AppDispatch, activeApplications])

  const checkIsEstateActionCodeExist = useMemo(()=>{
    if(config.bypassEstateActionCode) {
      return false;
    }
    const Estate_Action_Code_Enum = ["RS", "OT", "ME", "PT", "AJ", "RP", "SU", "SB", "CI", "SE"];
    
    if(smsInfo && Estate_Action_Code_Enum.includes(smsInfo?.financing_info?.estate_action_code)){
      return true;
    }
    else{
      return false;
    }
    
  },[smsInfo])

  const isSERSpostalCodeType1 = useMemo((): boolean => {
    const postalCode = values.selected_hdb_property?.address?.split(" ")?.slice(-1)[0] ?? "";
    const SERS_POSTAL_CODES_1 = [ // Blks 562-565 Ang Mo Kio Ave 3
      "560562", "560563", "560564", "560565",
    ]
    return SERS_POSTAL_CODES_1.includes(postalCode) && is_R2_151_Block_enabled;
  }, [values.selected_hdb_property?.hdb_ref, values.selected_hdb_property?.address])

  const isSERSpostalCodeType2 = useMemo((): boolean => {
    const postalCode = values.selected_hdb_property?.address?.split(" ")?.slice(-1)[0] ?? "";
    const SERS_POSTAL_CODES_2 = [ // Blks 212-218 Marsiling Crescent/Lane
      "730212", "730213", "730214", "730215", "730216", "730217", "730218",
    ]
    return SERS_POSTAL_CODES_2.includes(postalCode) && is_R2_151_Block_enabled;
  }, [values.selected_hdb_property?.hdb_ref, values.selected_hdb_property?.address])

  /**
   * Check if the selected hdb property address are matches with the current ongoing application api address.
   */
  const { getIsPropertyExisted, application_id } = useMemo(()=>{
    let applicationId = "0";
    if(activeApplications?.length > 0 && activeApplications[0].application_id && activeApplications[0].application_status !== "CLOSED"){
        applicationId = activeApplications[0].application_id;
    }
    return { getIsPropertyExisted: activeApplications?.length > 0, application_id: applicationId  };
  },[activeApplications])


  const navigateToApplication = useCallback(()=>{
    let timeoutId: number;
    timeoutId = window.setTimeout(() => {
      const {address, hdb_ref} = values.selected_hdb_property!;
      if(address && hdb_ref){
        setIsInit(false);
        window.location.href = "/transfer/application-coo";
      }
      else{
        setIsInit(false);
        alert("Oops, something wrong with the selected property.");
        navigate("/");
      }
    },1000);
   
    return () => {
      clearTimeout(timeoutId);
    }
  },[navigate, values.selected_hdb_property])

  const processNewApplication = useCallback(async()=>{
    if (!smsInfo?.holding_type?.trim()) {
      await PolymerAxios(originalLoginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, {
        uinfin: originalLoginUser?.uinfin,
        message: `[ Polymer FE -> MultipleProperties -> processNewApplication ]HDBref: ${values.selected_hdb_property?.hdb_ref ?? "null"}, UIN: ${originalLoginUser?.uinfin} blocked: current holding type is blank (HDBCHVMTYR-1024 / 311).`
      });
      alert("An error occurred. Please try again later.");
      return;
    }

    setIsInit(true);
 
    let singlePropertyObj = new Properties(values.selected_hdb_property);
    singlePropertyObj.decryptOwnersOccupiersAdditionalRoles();

    await AppDispatch(updateApplicationStartFormDataThunk({data:new ApplicationStart({ ...startApplicationFormData, has_outstanding_loan: hasOutstandingFees, selected_hdb_property: { ...singlePropertyObj, ...smsInfo } })}))

    let timeoutId: number;
    timeoutId = window.setTimeout(() => {
      navigateToApplication();
    },3000);
   
    return () => {
      clearTimeout(timeoutId);
    }
  
  },[AppDispatch, hasOutstandingFees, navigateToApplication, originalLoginUser?.uinfin, singpassToken, smsInfo, startApplicationFormData, values.selected_hdb_property])

  const handleContinueDraftApplication = useCallback(()=>{
    console.log('continue draft application_id', application_id);
    if(application_id){
      const url = `/application-coo?application_id=${application_id}`;
      console.log('navigating to:', url);
      navigate(url);    
    }
    else{
      console.log('process new application');
      processNewApplication()
    }
  },[application_id, navigate, processNewApplication])

  const processValidateNewApplication = useCallback(()=>{
    console.log('get is property existed', getIsPropertyExisted);
    if(getIsPropertyExisted){
      const draftStages = [
        "SAVE_DRAFT",
        "SUBMIT_COMPLETE",
        "GATHER_CONSENT_COMPLETE",
        "PENDING_PAYMENT"
      ]
      console.log('application:', activeApplications[0]);
      if(document.location.origin !== "https://services3.hdb.gov.sg" && document.location.origin !== "https://services3-stg.hdb.gov.sg"){
        setContinueDraftConfirmation(true);
        return;
      }
      if(draftStages.includes(activeApplications[0].application_status)){
        console.log('open draft')
        setContinueDraftConfirmation(true);
      }else{
        console.log('continue draft')
        handleContinueDraftApplication();
      }
    }
    else{
      console.log('process new application')
      processNewApplication()
    }
  },[getIsPropertyExisted, processNewApplication, handleContinueDraftApplication, activeApplications])

  const closeOngoingTransactionModal = useCallback(() => {
    setIsShowingOngoingTransactionModal(false);
  }, [setIsShowingOngoingTransactionModal])

  const processNext = useCallback(async () =>{
    const findBb13Application = existingBb13Applications.find(item => item.hdb_ref.toLowerCase() === values.selected_hdb_property?.hdb_ref.toLowerCase());
    if(findBb13Application && !config.byPassBB13){
        window.location.href = findBb13Application.link;
        return;
    }
    if(isError || isRdbError) {
      await PolymerAxios(originalLoginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, {
        uinfin: originalLoginUser?.uinfin,
        message: `[ Polymer FE -> MultipleProperties -> processNext ]HDBref: ${values.selected_hdb_property?.hdb_ref?values.selected_hdb_property?.hdb_ref:"null"}, UIN: ${originalLoginUser?.uinfin} is unable to create a new application for multi properties due to Error ${isError || isRdbError}`
      });
      alert("An error occurred. Please try again later.");
    }
    else if (checkIsEstateActionCodeExist || (mamsEligibilityData === false)) {
      setIsShowingOngoingTransactionModal(true);
      await PolymerAxios(originalLoginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, {
        uinfin: originalLoginUser?.uinfin,
        message: `[ Polymer FE -> MultipleProperties -> processNext ]HDBref: ${values.selected_hdb_property?.hdb_ref?values.selected_hdb_property?.hdb_ref:"null"}, UIN: ${originalLoginUser?.uinfin} is unable to create a new application for multi properties due to OngoingTransaction.`
      });
    }
    else if(hasInitiatedApplication === true && !config.allowMultipleApplicationDebug) {
      setHasApplicationInitiatedModal(true);
      await PolymerAxios(originalLoginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, {
        uinfin: originalLoginUser?.uinfin,
        message: `[ Polymer FE -> MultipleProperties -> processNext ]HDBref: ${values.selected_hdb_property?.hdb_ref?values.selected_hdb_property?.hdb_ref:"null"}, UIN: ${originalLoginUser?.uinfin} is unable to create a new application for multi properties due to HasApplicationInitiated.`
      });
    }
    else if(hasOutstandingFees === true) {
      setHasOutstandingFeesModal(true);
      await PolymerAxios(originalLoginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, {
        uinfin: originalLoginUser?.uinfin,
        message: `[ Polymer FE -> MultipleProperties -> processNext ]HDBref: ${values.selected_hdb_property?.hdb_ref?values.selected_hdb_property?.hdb_ref:"null"}, UIN: ${originalLoginUser?.uinfin} is unable to create a new application for multi properties due to HasOutstandingFees.`
      });
    }
    else if(isSERSpostalCodeType1 === true) {
      setHasSERSPostalCode1Modal(true);
      await PolymerAxios(originalLoginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, {
        uinfin: originalLoginUser?.uinfin,
        message: `[ Polymer FE -> MultipleProperties -> processNext ]HDBref: ${values.selected_hdb_property?.hdb_ref?values.selected_hdb_property?.hdb_ref:"null"}, UIN: ${originalLoginUser?.uinfin} is blocked from creating or accessing application flow for multi properties due to the announcement of SERS (R2-151) [Blks 562-565 Ang Mo Kio Ave 3].`
      });
    }
    else if(isSERSpostalCodeType2 === true) {
      setHasSERSPostalCode2Modal(true);
      await PolymerAxios(originalLoginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, {
        uinfin: originalLoginUser?.uinfin,
        message: `[ Polymer FE -> MultipleProperties -> processNext ]HDBref: ${values.selected_hdb_property?.hdb_ref?values.selected_hdb_property?.hdb_ref:"null"}, UIN: ${originalLoginUser?.uinfin} is blocked from creating or accessing application flow for multi properties due to the announcement of SERS (R2-151) [Blks 212-218 Marsiling Crescent/Lane].`
      });
    }
    else if (!!route) {
      navigate(route);
    }
    else if(getIsPropertyExisted){
      processValidateNewApplication();
    }
    else{
      processNewApplication();
    }
  },[checkIsEstateActionCodeExist, existingBb13Applications, hasOutstandingFees, mamsEligibilityData, hasInitiatedApplication, values.selected_hdb_property?.hdb_ref, processNewApplication, getIsPropertyExisted, processValidateNewApplication, isError, isRdbError, route, isSERSpostalCodeType1, isSERSpostalCodeType2, originalLoginUser?.uinfin, singpassToken])
  

  const handleModalPrimary = useCallback(()=>{
    setContinueDraftConfirmation(false);
    handleContinueDraftApplication();
  },[handleContinueDraftApplication])

  const removeApplication = useCallback(async () => {
    const application = activeApplications.find(item => item.application_id === application_id);
    if(!application){
      return;
    }

    const draftStages = [
      "SAVE_DRAFT",
      "SUBMIT_COMPLETE",
      "GATHER_CONSENT_COMPLETE",
      "PENDING_PAYMENT"
    ]
    if(draftStages.includes(application.application_status)){
      try {
        await PolymerAxios(originalLoginUser?.uinfin ?? "", singpassToken)
          .post(`${API_ApplicationRemove}/${application.application_id}`)
          .then((res) => res.data);
      } catch (error) {
        console.log(error);
      }
    }
  }, [singpassToken, application_id, originalLoginUser, activeApplications]);

  
  const handleModalSecondary = useCallback(()=>{
    removeApplication();
    processNewApplication();
    setContinueDraftConfirmation(false);
  },[processNewApplication, removeApplication])
 

  const handleHasOutstandingLoanPopUp = useCallback(()=>{
    setHasOutstandingFeesModal((prev) => !prev)
    processValidateNewApplication();
  },[processValidateNewApplication])

  const closeContinueDraftConfirmationModal = useCallback(() => {
    setContinueDraftConfirmation(false);
}, [])

  const closeOutstandingFeeModal = useCallback(() => {
    setHasOutstandingFeesModal(false);
  }, [])
  const closeHasInitiatedApplicationModal = useCallback(() => {
    setFormValue({ selected_hdb_property: undefined });
    setHasApplicationInitiatedModal(false);
  }, [setFormValue])

  const closeSERSPostalCodeModal = useCallback(() => {
    setHasSERSPostalCode1Modal(false);
    setHasSERSPostalCode2Modal(false);
  }, [])

  return (
    <>
      <P2Modal 
        isOpen={isInit}
        children={<LottieLoader/>}
      />
      <ApplicationCooLayout>
        <div className='polymer-start-application'>
          <P2TextDescription titleRequired color={'#333333'} title={'Please select an address.'} />
          <AddressSelection header={["Address", "Role"]} items={filteredProperties} onItemSelect={useCallback((value: Properties) => {
            setFormValue({ selected_hdb_property: value });
          },[setFormValue])} value={values.selected_hdb_property} style={{ marginTop: "16px" }} />
        </div>

        <P2ButtonGroup>
          <P2Button style={{marginBottom: "104px"}} variant={"primary-1"} disabled={!isBtnValid || isLoading} onClick={processNext}>{isLoading ? "Checking..." : "Next"}</P2Button>
        </P2ButtonGroup>

      </ApplicationCooLayout>
      <P2Modal
        isOpen={continueDraftConfirmation}
        onCloseModal={closeContinueDraftConfirmationModal}
        variant="default"
        title={`Create new application`}
        primaryButtonText="No, continue current application"
        secondaryButtonText="Yes, create new application"
        primaryButtonFunction={handleModalPrimary}
        secondaryButtonFunction={handleModalSecondary}
        children={<p>A draft application was initiated and saved on {getDraftApplicationDate}. Do you want to create a new application?</p>}
      />
      <P2Modal
        isOpen={hasOutstandingFeesModal}
        onCloseModal={closeOutstandingFeeModal}
        variant="default"
        title={`You having outstanding fee(s)`}
        primaryButtonText="Acknowledge"
        primaryButtonFunction={handleHasOutstandingLoanPopUp}
        children={<p>You have outstanding fees with HDB. Please make the necessary payments before initiating a change in flat ownership.</p>}
      />
      <P2Modal
        isOpen={hasApplicationInitiatedModal}
        onCloseModal={closeHasInitiatedApplicationModal}
        variant="default"
        title={`This property has been initiated by other applicant`}
        primaryButtonText="Acknowledge"
        primaryButtonFunction={closeHasInitiatedApplicationModal}
        children={<p>You cannot submit a change of flat ownership application because another applicant has already initiated a request for this property.</p>}
      />
      <P2Modal
        isOpen={hasSERSPostalCode1Modal}
        onCloseModal={closeSERSPostalCodeModal}
        variant="default"
        title={`You cannot start a new application`}
        primaryButtonText="Back"
        primaryButtonFunction={closeSERSPostalCodeModal}
        children={<p>Submission of application is not allowed. Please approach our Ang Mo Kio Branch for further assistance.</p>}
      />
      <P2Modal
        isOpen={hasSERSPostalCode2Modal}
        onCloseModal={closeSERSPostalCodeModal}
        variant="default"
        title={`You cannot start a new application`}
        primaryButtonText="Back"
        primaryButtonFunction={closeSERSPostalCodeModal}
        children={<p>Submission of application is not allowed. Please approach our Woodlands Branch for further assistance.</p>}
      />
      <P2Modal
        isOpen={isShowingOngoingTransactionModal}
        onCloseModal={closeOngoingTransactionModal}
        variant="default"
        title={`You cannot start a new application.`} 
        children={<><p>You cannot submit a request for the change in flat ownership because another transaction for the flat is currently in progress.</p></>}
      />
    </>

  )
}

export default PropertySelection